import styles from '../../css/components/poll.css?inline';

const POLL_NAME_PATTERN = /^[a-zA-Z0-9_-]{1,100}$/;

class Poll extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
  }

  connectedCallback() {
    if (this.initialized) {
      return;
    }
    this.initialized = true;
    this.name = this.getAttribute('name') || '';
    this.storageKey = `lachvegas:poll:${this.name}`;
    this.renderShell();

    if (!POLL_NAME_PATTERN.test(this.name)) {
      this.setStatus('Diese Umfrage ist momentan nicht verfügbar.');
      return;
    }

    this.loadPoll();
  }

  renderShell() {
    this.shadowRoot.innerHTML = `
            <style>${styles}</style>
            <section class="poll" aria-busy="true">
                <header class="poll__header">
                    <h2 class="poll__title"></h2>
                    <img class="poll__image" alt="" hidden>
                    <p class="poll__description" hidden></p>
                </header>
                <h3 class="poll__question"></h3>
                <div class="poll__content"></div>
                <p class="poll__status" role="status" aria-live="polite">Umfrage wird geladen …</p>
            </section>
        `;
    this.section = this.shadowRoot.querySelector('.poll');
    this.titleNode = this.shadowRoot.querySelector('.poll__title');
    this.imageNode = this.shadowRoot.querySelector('.poll__image');
    this.descriptionNode = this.shadowRoot.querySelector('.poll__description');
    this.questionNode = this.shadowRoot.querySelector('.poll__question');
    this.contentNode = this.shadowRoot.querySelector('.poll__content');
    this.statusNode = this.shadowRoot.querySelector('.poll__status');
  }

  async loadPoll() {
    try {
      const params = new URLSearchParams({ action: 'get_poll', name: this.name });
      const response = await fetch(`/api.php?${params}`, { cache: 'no-store' });
      const data = await response.json();
      if (!response.ok || !data.success || !data.poll) {
        throw new Error(data.message || 'Die Umfrage konnte nicht geladen werden.');
      }

      this.poll = data.poll;
      this.renderPoll();
    } catch (error) {
      this.section.setAttribute('aria-busy', 'false');
      this.setStatus(error.message || 'Die Umfrage konnte nicht geladen werden.');
    }
  }

  renderPoll() {
    this.titleNode.textContent = this.poll.title;
    this.questionNode.textContent = this.poll.question;
    this.descriptionNode.textContent = this.poll.description || '';
    this.descriptionNode.hidden = !this.poll.description;

    if (typeof this.poll.image === 'string' && this.poll.image.trim()) {
      try {
        const imageUrl = new URL(this.poll.image, window.location.origin);
        if (
          ['http:', 'https:'].includes(imageUrl.protocol)
                    && imageUrl.origin === window.location.origin
        ) {
          this.imageNode.src = imageUrl.href;
          this.imageNode.alt = this.poll.title;
          this.imageNode.hidden = false;
        }
      } catch {
        this.imageNode.hidden = true;
      }
    }

    this.section.setAttribute('aria-busy', 'false');
    if (this.hasSubmitted()) {
      this.renderResults();
      this.setStatus('Du hast bereits an dieser Umfrage teilgenommen. Danke!');
      return;
    }

    this.renderForm();
    if (this.storageUnavailable) {
      this.disableSubmit();
      this.setStatus('Die Abstimmungssperre kann in diesem Browser nicht gelesen werden.');
    } else {
      this.setStatus('');
    }
  }

  renderForm() {
    const form = document.createElement('form');
    form.className = 'poll__form';
    const hasOptions = Array.isArray(this.poll.options) && this.poll.options.length > 0;

    if (hasOptions) {
      const fieldset = document.createElement('fieldset');
      fieldset.className = 'poll__options';
      const legend = document.createElement('legend');
      legend.textContent = this.poll.allowMultiple
        ? 'Wähle eine oder mehrere Antworten'
        : 'Wähle eine Antwort';
      fieldset.append(legend);

      this.poll.options.forEach(option => {
        const label = document.createElement('label');
        label.className = 'poll__option';
        const input = document.createElement('input');
        input.type = this.poll.allowMultiple ? 'checkbox' : 'radio';
        input.name = 'poll-answer';
        input.value = option.id;
        input.required = !this.poll.allowMultiple;
        const text = document.createElement('span');
        text.textContent = option.text;
        label.append(input, text);
        fieldset.append(label);
      });
      form.append(fieldset);
    } else {
      const label = document.createElement('label');
      label.className = 'poll__answer-label';
      label.textContent = 'Deine Antwort';
      const textarea = document.createElement('textarea');
      textarea.name = 'answer';
      textarea.required = true;
      textarea.maxLength = 1000;
      textarea.rows = 4;
      label.append(textarea);
      form.append(label);
    }

    const submitButton = document.createElement('button');
    submitButton.className = 'poll__submit';
    submitButton.type = 'submit';
    submitButton.textContent = 'Abstimmen';
    form.append(submitButton);
    form.addEventListener('submit', event => this.submitVote(event, form, submitButton));
    this.contentNode.replaceChildren(form);
  }

  async submitVote(event, form, submitButton) {
    event.preventDefault();
    if (!form.reportValidity() || submitButton.disabled) {
      return;
    }

    let payload;
    if (this.poll.options.length > 0) {
      const optionIds = [...form.querySelectorAll('input:checked')].map(input => input.value);
      if (optionIds.length === 0) {
        this.setStatus('Bitte wähle mindestens eine Antwort aus.');
        return;
      }
      payload = { optionIds };
    } else {
      payload = { answer: form.elements.answer.value };
    }

    if (this.hasSubmitted()) {
      this.renderResults();
      this.setStatus('Du hast bereits an dieser Umfrage teilgenommen. Danke!');
      return;
    }
    if (this.storageUnavailable) {
      this.disableSubmit();
      this.setStatus('Deine Antwort wurde nicht gesendet, da die Abstimmungssperre nicht gelesen werden kann.');
      return;
    }

    try {
      window.localStorage.setItem(this.storageKey, 'pending');
    } catch {
      this.setStatus('Deine Antwort wurde nicht gesendet, da die Abstimmungssperre nicht gespeichert werden kann.');
      return;
    }

    submitButton.disabled = true;
    this.setStatus('Deine Antwort wird gespeichert …');
    try {
      const params = new URLSearchParams({ action: 'save_poll', name: this.name });
      const response = await fetch(`/api.php?${params}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      if (!response.ok || !data.success || !data.poll) {
        throw new Error(data.message || 'Deine Antwort konnte nicht gespeichert werden.');
      }

      this.poll = data.poll;
      try {
        window.localStorage.setItem(this.storageKey, 'submitted');
      } catch {
        this.storageUnavailable = true;
      }
      this.renderResults();
      this.setStatus(this.storageUnavailable
        ? 'Deine Antwort wurde gespeichert, aber die Abstimmungssperre kann nicht aktualisiert werden.'
        : 'Danke für deine Teilnahme!');
    } catch (error) {
      try {
        window.localStorage.removeItem(this.storageKey);
      } catch {
        this.setStatus('Deine Antwort konnte nicht gespeichert werden. Die Abstimmungssperre ließ sich nicht zurücksetzen.');
        return;
      }
      submitButton.disabled = false;
      this.setStatus(error.message || 'Deine Antwort konnte nicht gespeichert werden.');
    }
  }

  renderResults() {
    const results = document.createElement('div');
    results.className = 'poll__results';
    const responses = Number(this.poll.responses) || 0;

    if (this.poll.options.length > 0) {
      this.poll.options.forEach(option => {
        const votes = Number(option.votes) || 0;
        const percentage = responses > 0 ? Math.round((votes / responses) * 100) : 0;
        const row = document.createElement('div');
        row.className = 'poll__result';
        const label = document.createElement('div');
        label.className = 'poll__result-label';
        const answer = document.createElement('span');
        answer.textContent = option.text;
        const total = document.createElement('span');
        total.textContent = `${percentage}% (${votes})`;
        label.append(answer, total);
        const track = document.createElement('div');
        track.className = 'poll__bar-track';
        track.setAttribute('aria-label', `${option.text}: ${percentage}%`);
        const bar = document.createElement('div');
        bar.className = 'poll__bar';
        bar.style.width = `${Math.min(100, Math.max(0, percentage))}%`;
        track.append(bar);
        row.append(label, track);
        results.append(row);
      });
    } else {
      const answers = document.createElement('ul');
      answers.className = 'poll__text-results';
      (this.poll.textAnswers || []).forEach(answer => {
        const item = document.createElement('li');
        item.textContent = answer;
        answers.append(item);
      });
      results.append(answers);
    }

    const totalResponses = document.createElement('p');
    totalResponses.className = 'poll__total';
    totalResponses.textContent = `${responses} ${responses === 1 ? 'Stimme' : 'Stimmen'}`;
    results.append(totalResponses);
    this.contentNode.replaceChildren(results);
  }

  hasSubmitted() {
    try {
      return window.localStorage.getItem(this.storageKey) !== null;
    } catch {
      this.storageUnavailable = true;
      return false;
    }
  }

  disableSubmit() {
    const submitButton = this.contentNode.querySelector('.poll__submit');
    if (submitButton) {
      submitButton.disabled = true;
    }
  }

  setStatus(message) {
    this.statusNode.textContent = message;
  }
}

if (!customElements.get('lachvegas-poll')) {
  customElements.define('lachvegas-poll', Poll);
}

document.querySelectorAll('poll').forEach(placeholder => {
  const component = document.createElement('lachvegas-poll');
  [...placeholder.attributes].forEach(attribute => {
    component.setAttribute(attribute.name, attribute.value);
  });
  placeholder.replaceWith(component);
});
